import { useQuery, useMutation } from '@tanstack/react-query'
import { apiService } from '../lib/services/api'
import { AUDIT_LOG_ENDPOINTS } from './endpoints'
import type { AuditLog, AuditLogsListResponse, AuditActionsResponse } from '../lib/types'

// Hook to fetch audit logs with filtering
export const useAuditLogs = (
  page: number = 1,
  perPage: number = 20,
  filters?: {
    user_id?: string
    crisis_id?: string
    action?: string
    resource_type?: string
    resource_id?: string
    start_date?: string
    end_date?: string
  }
) => {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    per_page: perPage.toString(),
    ...(filters?.user_id && { user_id: filters.user_id }),
    ...(filters?.crisis_id && { crisis_id: filters.crisis_id }),
    ...(filters?.action && { action: filters.action }),
    ...(filters?.resource_type && { resource_type: filters.resource_type }),
    ...(filters?.resource_id && { resource_id: filters.resource_id }),
    ...(filters?.start_date && { start_date: filters.start_date }),
    ...(filters?.end_date && { end_date: filters.end_date }),
  })

  return useQuery({
    queryKey: ['audit-logs', page, perPage, filters],
    queryFn: async (): Promise<AuditLogsListResponse> => {
      return apiService.get(`${AUDIT_LOG_ENDPOINTS.LIST}?${queryParams}`)
    },
  })
}

// Hook to fetch current user's activity
export const useMyAuditActivity = (page: number = 1, perPage: number = 20) => {
  return useQuery({
    queryKey: ['my-audit-activity', page, perPage],
    queryFn: async (): Promise<AuditLogsListResponse> => {
      return apiService.get(`${AUDIT_LOG_ENDPOINTS.MY_ACTIVITY}?page=${page}&per_page=${perPage}`)
    },
  })
}

// Hook to fetch audit logs for a specific crisis
export const useCrisisAuditLogs = (crisisId: string, page: number = 1, perPage: number = 20) => {
  return useQuery({
    queryKey: ['crisis-audit-logs', crisisId, page, perPage],
    queryFn: async (): Promise<AuditLogsListResponse> => {
      return apiService.get(
        `${AUDIT_LOG_ENDPOINTS.CRISIS_LOGS(crisisId)}?page=${page}&per_page=${perPage}`
      )
    },
    enabled: !!crisisId,
  })
}

// Hook to fetch audit logs for a specific resource
export const useResourceAuditLogs = (
  resourceType: string,
  resourceId: string,
  page: number = 1,
  perPage: number = 20,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ['resource-audit-logs', resourceType, resourceId, page, perPage],
    queryFn: async (): Promise<AuditLogsListResponse> => {
      return apiService.get(
        `${AUDIT_LOG_ENDPOINTS.RESOURCE_LOGS(resourceType, resourceId)}?page=${page}&per_page=${perPage}`
      )
    },
    enabled: !!resourceType && !!resourceId && enabled,
  })
}

// Hook to fetch available audit actions
export const useAuditActions = () => {
  return useQuery({
    queryKey: ['audit-actions'],
    queryFn: async (): Promise<AuditActionsResponse> => {
      return apiService.get(AUDIT_LOG_ENDPOINTS.ACTIONS)
    },
  })
}

// Hook to fetch single audit log by ID
export const useAuditLog = (logId: string) => {
  return useQuery({
    queryKey: ['audit-log', logId],
    queryFn: async (): Promise<AuditLog> => {
      return apiService.get(AUDIT_LOG_ENDPOINTS.LOG_BY_ID(logId))
    },
    enabled: !!logId,
  })
}

// Hook to export audit logs
export const useExportAuditLogs = () => {
  return useMutation({
    mutationFn: async (filters?: {
      user_id?: string
      crisis_id?: string
      action?: string
      resource_type?: string
      start_date?: string
      end_date?: string
    }) => {
      const queryParams = new URLSearchParams({
        ...(filters?.user_id && { user_id: filters.user_id }),
        ...(filters?.crisis_id && { crisis_id: filters.crisis_id }),
        ...(filters?.action && { action: filters.action }),
        ...(filters?.resource_type && { resource_type: filters.resource_type }),
        ...(filters?.start_date && { start_date: filters.start_date }),
        ...(filters?.end_date && { end_date: filters.end_date }),
      })

      // Use direct fetch for blob response with proper base URL and auth
      const API_BASE_URL = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_BASE_URL ?? '')
      const authToken = localStorage.getItem('auth_token') || ''

      const response = await fetch(`${API_BASE_URL}${AUDIT_LOG_ENDPOINTS.EXPORT}?${queryParams}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`)
      }

      return response.blob()
    },
  })
}
